/**
 * MyQuestionsGuide: the visual guide of My Questions (/my-questions), opened by the "?" after the page's title
 * (HelpTitle helpId="my_questions"). The steps follow the page: create a question in a subject (the header's
 * CreateQuestionMenu), prepare a .docx file, upload it (UploadQuestionsDialog's form: the subject, the optional grade
 * that narrows the preview's topics, the file) and check its dry-run preview (the topic and subtopic for every
 * question or one question's own, the A / B / C difficulty, then "Save all"; into a subject only complete questions),
 * keep unfinished work as a draft, send it to review, see ticked questions as a student's test (the selection bar's
 * Preview, or the whole page's; drafts can't be ticked), fix a question returned for changes (its page's Edit and
 * Resubmit) and the Texts tab; then what the statuses mean, who does what and good to know (a subject's Tasks tab
 * only points to its own guide).
 * The preview step follows the page too: the table with its boxes from 768px, cards with theirs under it; under
 * 640px the bar's Clear is an × alone and the count line's "preview the page" an eye alone.
 * It follows the screen: phones (under 768px, the page's own breakpoint) have "Upload file" in the header's "⋯" (and
 * an open subject's in its "⋯"), and the question page's Edit across the page; under 640px (the form's breakpoint) the
 * form's buttons are stacked, the main one on top.
 * It follows the reader's roles (UserContext): the subject rule of the import (strict: only complete questions) for
 * those who write in subjects, the Tasks tip for those who see a subject's tasks.
 * Props, from the page: `places` (how many subjects, or "no folder", the reader creates in: with one the create
 * button opens the form at once, with several it asks where), `texts` (the Texts tab is there; without the passages
 * API it is not).
 */
import React, { useContext } from "react";
import { useTranslation } from "react-i18next";
import {
  ArrowLeftIcon,
  ArrowUpTrayIcon,
  BookOpenIcon,
  CheckBadgeIcon,
  DocumentTextIcon,
  EllipsisHorizontalIcon,
  EyeIcon,
  FolderIcon,
  KeyIcon,
  PaperAirplaneIcon,
  PencilIcon,
  PencilSquareIcon,
  PlusIcon,
  QueueListIcon,
  ShieldCheckIcon,
  TagIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { HelpFlow, HelpIntro, HelpLegend, HelpRoles, HelpStep, HelpSteps, HelpText, HelpTips } from "../HelpKit";
import {
  MiniBadge,
  MiniButton,
  MiniCard,
  MiniCheck,
  MiniChip,
  MiniDialog,
  MiniDivider,
  MiniField,
  MiniMenu,
  MiniRow,
  MiniScreen,
  MiniStack,
  MiniStatus,
  MiniTable,
  MiniTabs,
  MiniText,
} from "../Mini";
import { QuestionStatusBadge, statusText } from "../../QuestionList/questionStatus";
import { DIFFICULTY_POINTS } from "../../Difficulty/Difficulty";
import { useMediaQuery } from "../../../pages/Exams/TestBuilder/ui";
import { UserContext } from "../../../utils/contexts/UserContext";
import { canCreateIn, canSeeTasksIn, isAdminUser } from "../../../utils/folders";

// The statuses a question of My Questions goes through, in their order (the legend), as the page's status chips.
const LEGEND = ["DRAFT", "PENDING_REVIEW", "UNDER_REVIEW", "CHANGES_REQUESTED", "RESUBMITTED", "APPROVED", "DECLINED", "ARCHIVED"];

export default function MyQuestionsGuide({ places = 2, texts = true }) {
  const [t] = useTranslation();
  const { user } = useContext(UserContext) || {};
  // The page's own breakpoint: under it the header keeps "Create question" and puts "Upload file" in "⋯".
  const wide = useMediaQuery("(min-width: 768px)");
  // The question form's: under it its buttons are stacked, the main one on top.
  const roomy = useMediaQuery("(min-width: 640px)");
  const ex = (key) => t(`help.common.example.${key}`);
  const own = (key) => t(`help.my_questions.example.${key}`);
  const status = (code) => statusText(t, code);

  const isAdmin = isAdminUser(user);
  const folderRoles = user?.folder_roles || [];
  // Imports into a subject keep only complete questions (a subtopic and a difficulty each).
  const inSubjects = isAdmin || folderRoles.some(canCreateIn);
  // A subject's Tasks tab: its moderator and admins give tasks, authors and experts see their own.
  const seesTasks = isAdmin || folderRoles.some(canSeeTasksIn);

  const create = t("folders.create_question");
  const upload = t("buttons.upload_file");
  const back = t("buttons.back");
  const draft = t("my_questions.actions.save_draft_and_exit");
  const finish = t("my_questions.actions.finish_and_create");
  // The import's "Save all (N)" counts the reader's own file's questions.
  const saveAll = t("my_questions.upload.save_all", { count: "…" });
  const general = t("my_questions.upload.subtopics.general_label");
  const box = t("my_questions.upload.subtopics.question_label");
  const choose = t("my_questions.upload.subtopics.choose");
  // A previewed question's compact A / B / C picker ("A · 3").
  const levels = ["A", "B", "C"].map((level) => `${level} · ${DIFFICULTY_POINTS[level]}`);
  const preview = t("my_questions.preview.button", { count: 2 });
  // The preview's questions: two ticked, and a draft, which can't be.
  const ticked = [
    { key: "q1", name: ex("question_1"), status: "APPROVED", check: true },
    { key: "q2", name: ex("question_2"), status: "UNDER_REVIEW", check: true },
    { key: "q3", name: ex("question_3"), status: "DRAFT", check: false },
  ];
  const asks = places > 1;
  const file = `${ex("subject")}.docx`;

  return (
    <>
      <HelpIntro>{t("help.my_questions.intro")}</HelpIntro>

      <HelpFlow pipeline="bts" current="questions" />

      <HelpSteps>
        <HelpStep
          title={t("help.my_questions.steps.create.title")}
          illustration={
            <MiniScreen title={t("my_questions.title")}>
              <MiniRow className="justify-end gap-y-3">
                {wide && (
                  <MiniButton variant="quiet" icon={ArrowUpTrayIcon}>
                    {upload}
                  </MiniButton>
                )}
                <MiniButton variant="primary" icon={PlusIcon} chevron={asks} mark={asks ? "1" : true}>
                  {create}
                </MiniButton>
                {!wide && <MiniButton variant="outline" icon={EllipsisHorizontalIcon} />}
              </MiniRow>
              {asks && (
                <MiniRow className="justify-end">
                  <MiniMenu
                    title={t("folders.create_menu.title")}
                    items={[
                      { label: ex("exam"), icon: FolderIcon },
                      { label: ex("subject"), indent: true, mark: "2" },
                      { label: ex("subject_2"), indent: true },
                    ]}
                  />
                </MiniRow>
              )}
            </MiniScreen>
          }
        >
          <HelpText i18nKey={asks ? "help.my_questions.steps.create.text" : "help.my_questions.steps.create.text_one"} values={{ create }} />
          <p>
            <HelpText i18nKey="help.my_questions.steps.create.text_subject" values={{ create }} />
          </p>
        </HelpStep>

        <HelpStep
          title={t("help.my_questions.steps.file.title")}
          illustration={
            <MiniScreen title={file}>
              {/* The lines of the Word file, one row each. */}
              <MiniStack tight>
                <MiniRow>
                  <MiniText variant="strong">1. {own("question")}</MiniText>
                </MiniRow>
                <MiniRow>
                  <MiniText>A) {own("option_a")}</MiniText>
                </MiniRow>
                <MiniRow>
                  <MiniText>B) {own("option_b")}</MiniText>
                  <MiniBadge tone="green" mark={{ pointer: false }}>
                    /correct_answer/
                  </MiniBadge>
                </MiniRow>
                <MiniRow>
                  <MiniText>C) {own("option_c")}</MiniText>
                </MiniRow>
              </MiniStack>
            </MiniScreen>
          }
        >
          <p>{t("help.my_questions.steps.file.text")}</p>
          <p>
            <HelpText
              i18nKey="help.my_questions.steps.file.text_rules"
              values={{ kz: t("my_questions.upload.instruction_kz"), ru: t("my_questions.upload.instruction_ru") }}
            />
          </p>
        </HelpStep>

        <HelpStep
          title={t("help.my_questions.steps.upload.title")}
          illustration={
            <MiniDialog
              title={t("my_questions.upload.title")}
              footer={[
                <MiniButton key="preview" variant="primary" icon={EyeIcon} mark>
                  {t("my_questions.upload.preview")}
                </MiniButton>,
              ]}
            >
              <MiniField label={t("folders.subject_folder")} value={`${ex("exam")} · ${ex("subject")}`} select />
              {/* Optional, once the subject is known: the preview offers that grade's topics only. */}
              <MiniField label={t("my_questions.upload.grade")} value={t("questions_page.filters.grade_n", { grade: 9 })} select />
              <MiniCard tone="slate" icon={DocumentTextIcon}>
                <MiniText variant="strong" truncate>
                  {file}
                </MiniText>
              </MiniCard>
            </MiniDialog>
          }
        >
          <HelpText
            i18nKey="help.my_questions.steps.upload.text"
            values={{ upload, folder: t("folders.subject_folder"), preview: t("my_questions.upload.preview") }}
          />
          <p>
            <HelpText i18nKey="help.my_questions.steps.upload.text_grade" values={{ grade: t("my_questions.upload.grade") }} />
          </p>
          {!wide && (
            <p>
              <HelpText i18nKey="help.my_questions.steps.upload.text_phone" values={{ upload }} />
            </p>
          )}
        </HelpStep>

        <HelpStep
          title={t("help.my_questions.steps.check.title")}
          illustration={
            // The preview: the topic and subtopic for every question on top (1), a question with its difficulty and
            // its own topic box below (2): amber with nothing chosen where the subject requires a subtopic.
            <MiniDialog
              title={t("my_questions.upload.title")}
              footer={[
                <MiniButton key="back" variant="outline">
                  {back}
                </MiniButton>,
                <MiniButton key="save" variant="primary" icon={ArrowUpTrayIcon}>
                  {saveAll}
                </MiniButton>,
              ]}
            >
              <MiniCard tone="slate">
                <MiniText variant="strong">{general}</MiniText>
                <MiniRow className="gap-y-3">
                  <MiniField
                    label={t("folders.matrix.topic_label")}
                    placeholder={t("folders.matrix.topic_placeholder")}
                    select
                    className="flex-1"
                    mark="1"
                  />
                  <MiniField
                    label={t("my_questions.upload.subtopics.label")}
                    placeholder={t("my_questions.upload.subtopics.pick_topic_first")}
                    select
                    className="flex-1"
                  />
                </MiniRow>
              </MiniCard>
              <MiniRow className="justify-between">
                <MiniText variant="strong">1. {own("question")}</MiniText>
                <MiniTabs items={levels} current={1} />
              </MiniRow>
              {/* One wrapping row, as the box's: what is chosen, then its button at the end. */}
              <MiniCard tone={inSubjects ? "amber" : "slate"} icon={TagIcon}>
                <MiniRow className="justify-between gap-y-3">
                  <MiniRow>
                    <MiniText>{box}:</MiniText>
                    <MiniText variant={inSubjects ? "strong" : "muted"}>{t("my_questions.upload.subtopics.not_chosen")}</MiniText>
                  </MiniRow>
                  <MiniButton variant="outline" mark="2">
                    {choose}
                  </MiniButton>
                </MiniRow>
              </MiniCard>
            </MiniDialog>
          }
        >
          <HelpText
            i18nKey="help.my_questions.steps.check.text"
            values={{ general, box, choose, own: t("my_questions.upload.subtopics.choose_own") }}
          />
          <p>
            <HelpText
              i18nKey={inSubjects ? "help.my_questions.steps.check.text_save_subject" : "help.my_questions.steps.check.text_save"}
              values={{ save: saveAll }}
            />
          </p>
        </HelpStep>

        <HelpStep
          title={t("help.my_questions.steps.drafts.title")}
          illustration={
            <MiniStack>
              <MiniRow className="gap-y-3">
                <MiniChip selected count={12}>
                  {t("question_list.status_all")}
                </MiniChip>
                <MiniChip dot="slate" count={2} mark>
                  {status("DRAFT")}
                </MiniChip>
                <MiniChip dot="amber" count={5}>
                  {status("PENDING_REVIEW")}
                </MiniChip>
              </MiniRow>
              <MiniCard>
                <MiniText variant="strong" truncate>
                  {ex("question_2")}
                </MiniText>
                <MiniRow>
                  <MiniStatus code="DRAFT" />
                </MiniRow>
              </MiniCard>
            </MiniStack>
          }
        >
          <HelpText i18nKey="help.my_questions.steps.drafts.text" values={{ draft, status: status("DRAFT") }} />
        </HelpStep>

        <HelpStep
          title={t("help.my_questions.steps.send.title")}
          illustration={
            // The form's final view: Back on the left, the draft and the main button on the right.
            <MiniStack>
              {roomy ? (
                <MiniRow className="justify-between gap-y-3">
                  <MiniButton variant="outline" icon={ArrowLeftIcon}>
                    {back}
                  </MiniButton>
                  <MiniRow className="justify-end gap-y-3">
                    <MiniButton variant="outline">{draft}</MiniButton>
                    <MiniButton variant="primary" mark>
                      {finish}
                    </MiniButton>
                  </MiniRow>
                </MiniRow>
              ) : (
                // Phones: the main button on top (marked without the cursor, which would cover the one below), Back last.
                <MiniStack>
                  <MiniRow>
                    <MiniButton variant="primary" mark={{ pointer: false }}>
                      {finish}
                    </MiniButton>
                  </MiniRow>
                  <MiniRow>
                    <MiniButton variant="outline">{draft}</MiniButton>
                  </MiniRow>
                  <MiniRow>
                    <MiniButton variant="outline" icon={ArrowLeftIcon}>
                      {back}
                    </MiniButton>
                  </MiniRow>
                </MiniStack>
              )}
              <MiniCard aside={<MiniStatus code="PENDING_REVIEW" />}>
                <MiniText variant="strong" truncate>
                  {ex("question_1")}
                </MiniText>
              </MiniCard>
            </MiniStack>
          }
        >
          <HelpText
            i18nKey="help.my_questions.steps.send.text"
            values={{ next: t("buttons.next"), finish, pending: status("PENDING_REVIEW") }}
          />
        </HelpStep>

        <HelpStep
          title={t("help.my_questions.steps.preview.title")}
          illustration={
            // The selection bar in the count line's place (its main button is the preview), the ticked questions under
            // it, and what opens: the test as a student sees it, with "Show the right answers".
            <MiniStack>
              <MiniCard tone="blue">
                {roomy ? (
                  <MiniRow className="justify-between gap-y-3">
                    <MiniRow>
                      <MiniText variant="strong">{t("folders.move.selected", { count: 2 })}</MiniText>
                      <MiniButton variant="quiet" icon={XMarkIcon}>
                        {t("folders.move.clear")}
                      </MiniButton>
                    </MiniRow>
                    <MiniButton variant="primary" icon={EyeIcon} mark>
                      {preview}
                    </MiniButton>
                  </MiniRow>
                ) : (
                  <MiniRow className="justify-between flex-nowrap">
                    <MiniRow className="flex-nowrap">
                      <MiniButton variant="quiet" icon={XMarkIcon} />
                      <MiniText variant="strong" truncate>
                        {t("folders.move.selected", { count: 2 })}
                      </MiniText>
                    </MiniRow>
                    <MiniButton variant="primary" icon={EyeIcon} mark>
                      {preview}
                    </MiniButton>
                  </MiniRow>
                )}
              </MiniCard>
              {wide ? (
                <MiniTable
                  columns={[{ label: "", width: "2.5rem" }, t("question_list.columns.question"), { label: t("question_list.columns.status"), width: "34%" }]}
                  rows={ticked.map((q) => [
                    <MiniCheck checked={q.check} />,
                    <MiniText variant="strong" truncate>
                      {q.name}
                    </MiniText>,
                    <MiniStatus code={q.status} />,
                  ])}
                />
              ) : (
                ticked.slice(0, 2).map((q) => (
                  <MiniCard key={q.key} icon={<MiniCheck checked />}>
                    <MiniText variant="strong" truncate>
                      {q.name}
                    </MiniText>
                    <MiniRow>
                      <MiniStatus code={q.status} />
                    </MiniRow>
                  </MiniCard>
                ))
              )}
              <MiniScreen title={t("my_questions.preview.name", { count: 2 })}>
                <MiniRow className="justify-between gap-y-3">
                  <MiniBadge tone="blue" icon={EyeIcon}>
                    {t("my_questions.preview.badge")}
                  </MiniBadge>
                  <MiniCheck>{t("test_bank.preview.show_key")}</MiniCheck>
                </MiniRow>
                <MiniCard>
                  <MiniText variant="strong" truncate>
                    1. {ex("question_1")}
                  </MiniText>
                </MiniCard>
              </MiniScreen>
            </MiniStack>
          }
        >
          <HelpText
            i18nKey="help.my_questions.steps.preview.text"
            values={{ preview, key: t("test_bank.preview.show_key") }}
          />
          <p>
            {/* Under sm the count line's button is an eye alone. */}
            <HelpText
              i18nKey={roomy ? "help.my_questions.steps.preview.text_page" : "help.my_questions.steps.preview.text_page_phone"}
              values={{ page: t("my_questions.preview.page_button") }}
            />
          </p>
        </HelpStep>

        <HelpStep
          title={t("help.my_questions.steps.returned.title")}
          illustration={
            // The question's page: Edit is its main button, at the top (on the right; across the page on phones);
            // Resubmit closes the reviewer's feedback card.
            <MiniStack>
              <MiniRow className={wide ? "justify-end gap-y-3" : "gap-y-3"}>
                <MiniButton variant="primary" icon={PencilSquareIcon} mark="1">
                  {t("buttons.edit")}
                </MiniButton>
              </MiniRow>
              <MiniCard>
                <MiniRow className="justify-between">
                  <MiniText variant="strong">{t("question_detail.review.feedback.changes_requested_title")}</MiniText>
                  <MiniStatus code="CHANGES_REQUESTED" />
                </MiniRow>
                <MiniCard tone="slate">
                  <MiniRow>
                    <MiniText variant="muted">{t("question_detail.review.feedback.reviewer_comment")}</MiniText>
                  </MiniRow>
                  <MiniRow>
                    <MiniText>{own("comment")}</MiniText>
                  </MiniRow>
                </MiniCard>
                <MiniDivider />
                <MiniRow className="justify-end gap-y-3">
                  <MiniButton variant="primary" icon={PaperAirplaneIcon} mark="2">
                    {t("question_detail.transitions.resubmit")}
                  </MiniButton>
                </MiniRow>
              </MiniCard>
            </MiniStack>
          }
        >
          <HelpText
            i18nKey="help.my_questions.steps.returned.text"
            values={{
              changes: status("CHANGES_REQUESTED"),
              edit: t("buttons.edit"),
              save: t("my_questions.actions.save_and_exit"),
            }}
          />
          <p>
            <HelpText i18nKey="help.my_questions.steps.returned.text_resubmit" values={{ resubmit: t("question_detail.transitions.resubmit") }} />
          </p>
        </HelpStep>

        {texts && (
          <HelpStep
            title={t("help.my_questions.steps.texts.title")}
            illustration={
              // The Texts tab, and a passage opened from it: its dialog's questions section has the create link.
              <MiniScreen title={t("my_questions.title")}>
                <MiniTabs
                  items={[
                    { label: t("passages.tab_questions"), icon: QueueListIcon },
                    { label: t("passages.tab_texts"), icon: BookOpenIcon },
                  ]}
                  current={1}
                />
                <MiniDialog title={t("passages.dialog.title")}>
                  <MiniCard tone="slate" icon={BookOpenIcon}>
                    <MiniText truncate>{ex("passage")}</MiniText>
                  </MiniCard>
                  <MiniDivider />
                  <MiniRow className="justify-between gap-y-3">
                    <MiniText variant="strong">{t("passages.dialog.questions")}</MiniText>
                    <MiniButton variant="link" icon={PlusIcon} mark>
                      {t("passages.dialog.create_question")}
                    </MiniButton>
                  </MiniRow>
                </MiniDialog>
              </MiniScreen>
            }
          >
            <HelpText
              i18nKey="help.my_questions.steps.texts.text"
              values={{ texts: t("passages.tab_texts"), create: t("passages.dialog.create_question") }}
            />
          </HelpStep>
        )}
      </HelpSteps>

      <HelpLegend
        rows={LEGEND.map((code) => ({
          key: code,
          badge: <QuestionStatusBadge status={{ code }} />,
          text: t(`help.my_questions.legend.${code.toLowerCase()}`),
        }))}
      />

      <HelpRoles
        rows={[
          { key: "admin", role: t("folders.roles.admin"), Icon: KeyIcon, can: t("help.my_questions.roles.admin") },
          { key: "moderator", role: t("folders.roles.moderator"), Icon: ShieldCheckIcon, can: t("help.my_questions.roles.moderator") },
          { key: "expert", role: t("folders.roles.expert"), Icon: CheckBadgeIcon, can: t("help.my_questions.roles.expert") },
          { key: "author", role: t("folders.roles.author"), Icon: PencilIcon, can: t("help.my_questions.roles.author") },
        ]}
      />

      <HelpTips
        items={[
          t("help.my_questions.tips.mark"),
          // Phones have an open subject's "Upload from file" in its "⋯" (FolderBar).
          <HelpText
            key="subject_upload"
            i18nKey={wide ? "help.my_questions.tips.subject_upload" : "help.my_questions.tips.subject_upload_phone"}
            values={{ upload: t("folders.upload_from_file") }}
          />,
          t("help.my_questions.tips.booklet"),
          // Only a pointer: the Tasks tab has a guide of its own.
          seesTasks && <HelpText key="tasks" i18nKey="help.my_questions.tips.tasks" values={{ tasks: t("folders.tab_tasks") }} />,
        ]}
      />
    </>
  );
}
